import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, Truck, ChevronDown, User } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();

  const isActive = (path) => location.pathname === path;

  // Close menu on route change
  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  // Lock body scroll while mobile menu is open so users don't get double-scroll confusion
  useEffect(() => {
    if (mobileMenuOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [mobileMenuOpen]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { 
      name: 'Services', 
      path: '/services',
      dropdown: [
        { name: 'Full Truckload (FTL)', path: '/services/ftl' },
        { name: 'Less Than Truckload (LTL)', path: '/services/ltl' },
        { name: 'Intermodal', path: '/services/intermodal' },
        { name: 'Drayage', path: '/services/drayage' },
        { name: 'Refrigerated Shipments', path: '/services/reefer' },
        { name: 'Air Freight', path: '/services/air-freight' },
        { name: 'Ocean Freight', path: '/services/ocean-freight' },
        { name: 'Warehousing', path: '/services/warehousing' },
        { name: 'Cross-Border', path: '/services/cross-border' },
        { name: 'Customs Clearance', path: '/services/customs-clearance' },
        { name: 'Hazmat Shipping', path: '/services/hazmat' },
        { name: 'Oversize & Heavy Haul', path: '/services/oversize' },
      ]
    },
    { name: 'Quote Calculator', path: '/calculator' },
    { name: 'Careers', path: '/careers' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact Us', path: '/contact' },
  ];

  return (
    <header className="sticky top-0 z-50">
      {/* Top Bar */}
      <div className="bg-zinc-900 text-white py-2 px-4 hidden md:block">
        <div className="max-w-7xl mx-auto flex justify-between items-center text-sm">
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-2">
              <Phone className="w-4 h-4" />
              <a href="tel:+18776809272" className="hover:text-red-400 transition-colors" data-testid="topbar-phone">
                1 (877) 680-9272
              </a>
            </span>
            <span className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              <a href="mailto:support@redballxpress.us" className="hover:text-red-400 transition-colors" data-testid="topbar-email">
                support@redballxpress.us
              </a>
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-zinc-400">Mon - Fri / 08:00 - 18:00</span>
            <Link 
              to="/quote" 
              className="bg-red-600 hover:bg-red-700 px-4 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors"
              data-testid="topbar-quote-btn"
            >
              Get a Quote
            </Link>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="bg-white/95 backdrop-blur-md border-b border-zinc-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 lg:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
              <img 
                src="/logo.png" 
                alt="Red Ball Express Logistics" 
                className="h-12 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                link.dropdown ? (
                  <div 
                    key={link.name}
                    className="relative"
                    onMouseEnter={() => setServicesOpen(true)}
                    onMouseLeave={() => setServicesOpen(false)}
                  >
                    <Link
                      to={link.path}
                      className={`flex items-center gap-1 px-4 py-2 text-sm font-semibold uppercase tracking-wide transition-colors ${
                        isActive(link.path) ? 'text-red-600' : 'text-zinc-700 hover:text-red-600'
                      }`}
                      data-testid={`nav-${link.name.toLowerCase()}`}
                    >
                      {link.name}
                      <ChevronDown className="w-4 h-4" />
                    </Link>
                    {servicesOpen && (
                      <div className="absolute top-full left-0 w-[420px] bg-white border border-zinc-200 shadow-lg py-3 grid grid-cols-2 gap-0">
                        {link.dropdown.map((item) => (
                          <Link
                            key={item.path}
                            to={item.path}
                            className="block px-4 py-2 text-sm text-zinc-700 hover:bg-zinc-50 hover:text-red-600 transition-colors"
                            data-testid={`nav-service-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`px-4 py-2 text-sm font-semibold uppercase tracking-wide transition-colors ${
                      isActive(link.path) ? 'text-red-600' : 'text-zinc-700 hover:text-red-600'
                    }`}
                    data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                )
              ))}
            </div>

            {/* CTA Buttons - Desktop */}
            <div className="hidden lg:flex items-center gap-3">
              <Link
                to="/load-board"
                className={`flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 text-sm font-bold uppercase tracking-wide transition-colors ${
                  isActive('/load-board') ? 'ring-2 ring-red-400 ring-offset-1' : ''
                }`}
                data-testid="nav-view-loads-btn"
              >
                <Truck className="w-4 h-4" />
                Load Board LIVE!
              </Link>
              {isAuthenticated && (
                <Link
                  to="/dashboard"
                  className="w-9 h-9 bg-zinc-900 hover:bg-zinc-800 text-white flex items-center justify-center rounded-full transition-colors"
                  data-testid="nav-dashboard-btn"
                  title="Dashboard"
                >
                  <User className="w-4 h-4" />
                </Link>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-zinc-700"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div
            className="lg:hidden border-t border-zinc-200 bg-white overflow-y-auto overscroll-contain"
            style={{ maxHeight: 'calc(100dvh - 4rem)', WebkitOverflowScrolling: 'touch' }}
            data-testid="mobile-menu-panel"
          >
            <div className="px-4 py-4 space-y-2 pb-8">
              {navLinks.map((link) => (
                <div key={link.name}>
                  <Link
                    to={link.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block px-4 py-3 text-sm font-semibold uppercase tracking-wide ${
                      isActive(link.path) ? 'text-red-600 bg-red-50' : 'text-zinc-700'
                    }`}
                    data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                  </Link>
                  {link.dropdown && (
                    <div className="ml-4 border-l-2 border-zinc-200">
                      {link.dropdown.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => setMobileMenuOpen(false)}
                          className="block px-4 py-2 text-sm text-zinc-600"
                          data-testid={`mobile-nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div className="pt-4 flex flex-col gap-2">
                <a href="tel:+18776809272" className="flex items-center gap-2 px-4 py-2 text-sm text-zinc-600">
                  <Phone className="w-4 h-4" /> 1 (877) 680-9272
                </a>
                <Link
                  to="/load-board"
                  onClick={() => setMobileMenuOpen(false)}
                  className="bg-red-600 text-white text-center py-3 text-sm font-bold uppercase tracking-wide"
                  data-testid="mobile-loadboard-btn"
                >
                  Load Board LIVE!
                </Link>
                {isAuthenticated && (
                  <Link
                    to="/dashboard"
                    onClick={() => setMobileMenuOpen(false)}
                    className="bg-zinc-900 text-white text-center py-3 text-sm font-bold uppercase tracking-wide"
                    data-testid="mobile-dashboard-btn"
                  >
                    Dashboard
                  </Link>
                )}
                <Link
                  to="/quote"
                  onClick={() => setMobileMenuOpen(false)}
                  className="border border-red-600 text-red-600 text-center py-3 text-sm font-bold uppercase tracking-wide"
                  data-testid="mobile-quote-btn"
                >
                  Get a Quote
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Navbar;
